import type { Locale } from "@/i18n/config";

export type StudioType =
  | "RECORDING_STUDIO"
  | "MIXING_STUDIO"
  | "MASTERING_STUDIO";

export type ProductStatus = "DRAFT" | "ACTIVE" | "INACTIVE" | "ARCHIVED";

/**
 * UI-facing product view — mapped from the shared `Product` table.
 * Only fields that exist in the DB are exposed; localized copy falls
 * back to English for locales without DB columns (ja / zh).
 */
export interface ProductView {
  id: string;
  nameKo: string;
  nameEn: string;
  descriptionKo: string | null;
  descriptionEn: string | null;
  thumbnailUrl: string | null;
  status: ProductStatus;
  minGuests: number;
  maxGuests: number;
  /** Included guests covered by the base price */
  baseGuests: number;
  /** Per-extra-guest surcharge rate (%) applied to the unit price */
  extraGuestRatio: number;
  durationMinutes: number;
  bufferBefore: number;
  bufferAfter: number;
  price: { amount: number; currency: string } | null;
  studioId: string | null;
  studioType: StudioType | null;
  branchId: string | null;
  brandId: string | null;
  minAdvanceDays: number;
  maxAdvanceDays: number | null;
  policy: {
    minAdvanceMinutes: number;
    minAdvanceDays: number;
    maxAdvanceDays: number;
    validFrom?: string | null;
    validTo?: string | null;
  };
}

/** Localized name with graceful fallback (ko → ko, others → en) */
export function productName(
  product: Pick<ProductView, "nameKo" | "nameEn">,
  locale: Locale,
): string {
  return locale === "ko" ? product.nameKo : (product.nameEn || product.nameKo);
}

export function branchName(
  branch: { name: string; nameEn?: string | null } | null | undefined,
  locale: Locale,
): string {
  if (!branch) return "";
  return locale === "ko" ? branch.name : (branch.nameEn || branch.name);
}

export function studioName(
  studio: { name: string; nameEn?: string | null } | null | undefined,
  locale: Locale,
): string {
  if (!studio) return "";
  return locale === "ko" ? studio.name : (studio.nameEn || studio.name);
}

export function productDescription(
  product: Pick<ProductView, "descriptionKo" | "descriptionEn">,
  locale: Locale,
): string {
  return (locale === "ko" ? product.descriptionKo : product.descriptionEn) ?? "";
}

/** Maps a studio type to its list-filter dictionary key */
export function studioTypeToFilterKey(
  type: StudioType | null,
): string {
  switch (type) {
    case "RECORDING_STUDIO":
      return "filterRecording";
    case "MIXING_STUDIO":
      return "filterMixing";
    case "MASTERING_STUDIO":
      return "filterMastering";
    default:
      return "filterAll";
  }
}

export type ReservationStatus =
  | "PENDING"
  | "CONFIRMED"
  | "IN_PROGRESS"
  | "COMPLETED"
  | "CANCELLED"
  | "NO_SHOW";

/** Plain reservation record returned by the store (no Prisma types leak) */
export interface ReservationRecord {
  reservationNo: string;
  productId: string;
  startAt: string; // ISO UTC
  endAt: string; // ISO UTC
  guestsCount: number;
  status: ReservationStatus;
  customerName: string;
  customerEmail: string;
  songTitle: string | null;
  artistName: string | null;
  songUrl: string | null;
  unitPrice: number;
  total: number;
  currency: string;
}
